import { Button, Input, TextLink } from '@olytic/foundation';
import { type MouseEvent, useEffect, useState } from 'react';

import { CloseGlyph } from './icons';
import type { PanelItem, PanelModel } from './model';
import type { ResourceRole } from './resourcingApi';
import { RoleSplitCard } from './RoleSplitCard';

interface Props {
  panel: PanelModel;
  /** The assignment being moved, so its row reads as active. */
  movingId: string | null;
  onMove: (item: PanelItem) => void;
  onPrimary: () => void;
  onClose: () => void;
  /** Client-side navigation for a plain click on a link. */
  onNavigate: (href: string, event: MouseEvent) => void;
  /** Saves a month's new hours. */
  onEditHours: (id: string, hours: number) => void;
  /** For the role split: every role, and a reload after the split changes. */
  roles: ResourceRole[];
  onChanged: () => void;
}

const MAX_HOURS = 744;

/** A month's hours, edited in place: saved on Enter or leaving the field, put back on Escape. */
function HoursField({ id, hours, label, onSave, onEditing }: {
  id: string;
  hours: number;
  label: string;
  onSave: (id: string, hours: number) => void;
  onEditing: (editing: boolean) => void;
}) {
  const [draft, setDraft] = useState(String(hours));
  useEffect(() => setDraft(String(hours)), [hours]);
  const value = Number(draft);
  const valid = draft.trim() !== '' && Number.isFinite(value) && value > 0 && value <= MAX_HOURS;
  const commit = () => {
    if (!valid) setDraft(String(hours));
    else if (Math.round(value * 10) / 10 !== hours) onSave(id, Math.round(value * 10) / 10);
  };
  return (
    <Input
      type="number"
      inputMode="decimal"
      min={0.5}
      max={MAX_HOURS}
      step={1}
      className="w-24"
      aria-label={`Hours on ${label}`}
      aria-invalid={!valid}
      value={draft}
      endSlot="h"
      draggable={false}
      onChange={(event) => setDraft(event.target.value)}
      onFocus={() => onEditing(true)}
      onBlur={() => {
        onEditing(false);
        commit();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          setDraft(String(hours));
          // Blurring after the reset saves nothing: the draft matches again.
          setTimeout(() => (event.target as HTMLInputElement).blur());
        }
      }}
    />
  );
}

/** The planner's detail column: what is resourced in the selected cell, each assignment movable to someone else. */
export function DetailsAside({ panel, movingId, onMove, onPrimary, onClose, onNavigate, onEditHours, roles, onChanged }: Props): JSX.Element {
  const go = (href: string) => (event: MouseEvent) => onNavigate(href, event);
  // A row being typed in is not dragged, so its text can be selected.
  const [editing, setEditing] = useState<string | null>(null);
  return (
    <aside aria-label="Details" className="flex max-w-full flex-[1_1_320px] flex-col border-0 border-l border-solid border-[var(--border)] bg-[var(--surface)]">
      <div className="flex items-start justify-between gap-3 border-0 border-b border-solid border-[var(--border)] px-5 pb-4 pt-3.5">
        <div className="min-w-0">
          <div className="oly-eyebrow">{panel.eyebrow}</div>
          <div className="mt-1 text-xl [font-weight:var(--title-weight)] text-[var(--fg-strong)]">{panel.title}</div>
          <div className="text-xs text-[var(--fg-muted)]">{panel.sub}</div>
        </div>
        <Button size="icon-sm" variant="subtle" aria-label="Close details" onClick={onClose}>
          <CloseGlyph />
        </Button>
      </div>
      <div className="flex flex-col gap-4 px-5 pb-5 pt-4">
        <div className="grid grid-cols-3 overflow-hidden rounded-[var(--radius-lg)] border border-solid border-[var(--border)]">
          {panel.stats.map((s) => (
            <div key={s.label} className="border-0 border-r border-solid border-[var(--border-subtle)] px-3 py-2">
              <div className="text-[11px] font-emphasis uppercase tracking-[var(--collabel-tracking)] text-[var(--fg-muted)]">{s.label}</div>
              <div className={`text-lg tabular-nums [font-weight:var(--title-weight)] ${s.over ? 'text-[var(--heat-over-fg)]' : 'text-[var(--fg-strong)]'}`}>{s.value}</div>
            </div>
          ))}
        </div>
        {panel.note ? (
          <div className="rounded-[var(--radius-md)] bg-[var(--heat-over-1)] px-2.5 py-2 text-xs text-[var(--heat-over-fg)]">{panel.note}</div>
        ) : null}
        {panel.sections.map((section) => (
          <div key={section.title}>
            <div className="border-0 border-b border-solid border-[var(--rule)] pb-1.5 text-[11px] font-emphasis uppercase tracking-[var(--collabel-tracking)] text-[var(--fg-muted)]">
              {section.title}
            </div>
            {!section.items.length ? <div className="py-2.5 text-[var(--fg-muted)]">{section.empty}</div> : null}
            {section.items.map((item) => {
              const active = !!item.move && item.move.assignment.id === movingId;
              return (
                <div
                  key={item.key}
                  draggable={!!item.move && editing !== item.key}
                  onDragStart={item.move ? (event) => {
                    event.dataTransfer.setData('text/plain', item.key);
                    event.dataTransfer.effectAllowed = 'move';
                    if (!active) onMove(item);
                  } : undefined}
                  className={`flex justify-between gap-2 border-0 border-b border-solid border-[var(--border-subtle)] py-2 ${item.move ? 'cursor-grab' : ''} ${active ? 'bg-[var(--accent-soft)]' : ''}`}
                >
                  {item.move ? <span aria-hidden className="flex-none pt-0.5 text-[var(--fg-muted)]">⋮⋮</span> : null}
                  <div className="min-w-0 flex-1">
                    {item.href ? (
                      <TextLink href={item.href} onClick={go(item.href)} className="font-emphasis no-underline">{item.title}</TextLink>
                    ) : (
                      <div className="font-emphasis text-[var(--fg-strong)]">{item.title}</div>
                    )}
                    {item.sub ? <div className="text-xs text-[var(--fg-muted)]">{item.sub}</div> : null}
                  </div>
                  <div className="flex-none text-right">
                    {item.edit ? (
                      <HoursField
                        id={item.edit.id}
                        hours={item.edit.hours}
                        label={item.title}
                        onSave={onEditHours}
                        onEditing={(on) => setEditing(on ? item.key : null)}
                      />
                    ) : (
                      <div className="font-emphasis tabular-nums text-[var(--fg-strong)]">{item.value}</div>
                    )}
                    {item.valueSub ? <div className="text-xs text-[var(--fg-muted)]">{item.valueSub}</div> : null}
                    {item.move ? (
                      <Button size="sm" className="mt-1" aria-label={`${active ? 'Cancel moving' : item.move.verb} ${item.title}`} onClick={() => onMove(item)}>
                        {active ? 'Cancel' : item.move.verb}
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        {panel.splitFor ? <RoleSplitCard opportunityId={panel.splitFor} roles={roles} onChanged={onChanged} /> : null}
        <div className="flex flex-wrap gap-2">
          {panel.primary.href ? (
            <Button asChild variant="brand">
              <a href={panel.primary.href} onClick={go(panel.primary.href)}>{panel.primary.label}</a>
            </Button>
          ) : (
            <Button variant="brand" onClick={onPrimary}>{panel.primary.label}</Button>
          )}
          {panel.secondary ? (
            <Button asChild>
              <a href={panel.secondary.href} onClick={go(panel.secondary.href)}>{panel.secondary.label}</a>
            </Button>
          ) : null}
        </div>
      </div>
    </aside>
  );
}
